// DESCRIPTION: SortButton — a clickable column-header label with an asc/desc arrow.
// DESCRIPTION: Shared by the events and bookings admin list tables. Design-system tokens only.

import { __ } from '@wordpress/i18n';

/**
 * A sortable column-header button.
 *
 * Clicking an inactive column activates it with its `defaultDirection`.
 * Clicking the active column toggles ASC/DESC. The active column shows a
 * solid arrow; inactive sortable columns show a muted arrow hint.
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {string}   props.label              Visible column label.
 * @param {string}   props.column             Sort key this column emits (e.g. 'title', 'date').
 * @param {string}   props.activeColumn       Currently active sort key.
 * @param {string}   props.direction          Current direction ('ASC' | 'DESC').
 * @param {string}   [props.defaultDirection] Direction to use when first activating this column.
 * @param {string}   [props.className]        Extra classes for the label (typography/size), e.g. table header style.
 * @param {Function} props.onSortChange       Called with ( column, direction ).
 *
 * @return {JSX.Element} The SortButton component.
 */
const SortButton = ( {
	label,
	column,
	activeColumn,
	direction,
	defaultDirection = 'ASC',
	className = '',
	onSortChange,
} ) => {
	const isActive = column === activeColumn;

	const handleClick = () => {
		if ( isActive ) {
			onSortChange( column, direction === 'ASC' ? 'DESC' : 'ASC' );
		} else {
			onSortChange( column, defaultDirection );
		}
	};

	const baseClass =
		'nb-bg-transparent nb-border-0 nb-p-0 nb-cursor-pointer nb-inline-flex nb-items-center nb-gap-1 nb-transition-colors nb-duration-fast';
	const colorClass = isActive
		? 'nb-text-neutral-900'
		: 'nb-text-neutral-500 hover:nb-text-neutral-900';
	const typographyClass =
		className ||
		'nb-text-xs nb-font-semibold nb-uppercase nb-tracking-wide';
	const arrowClass = isActive
		? 'nb-text-neutral-900'
		: 'nb-text-neutral-300 group-hover:nb-text-neutral-500';

	return (
		<button
			type="button"
			className={ `${ baseClass } ${ colorClass } ${ typographyClass }` }
			onClick={ handleClick }
			aria-sort={
				isActive
					? direction === 'ASC'
						? 'ascending'
						: 'descending'
					: undefined
			}
		>
			<span>{ label }</span>
			<svg
				className={ `nb-w-3 nb-h-3 nb-shrink-0 nb-transition-transform nb-duration-fast ${ arrowClass }` }
				fill="none"
				viewBox="0 0 12 12"
				aria-hidden="true"
			>
				{ isActive && direction === 'DESC' ? (
					<path
						d="M6 9.5V2.5M6 2.5L3 5.5M6 2.5L9 5.5"
						stroke="currentColor"
						strokeWidth="1.4"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				) : (
					<path
						d="M6 2.5v7M6 9.5l3-3M6 9.5l-3-3"
						stroke="currentColor"
						strokeWidth="1.4"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				) }
			</svg>
			<span className="nb-sr-only">
				{ __( 'Click to change sort direction', 'nemtly-booking' ) }
			</span>
		</button>
	);
};

export default SortButton;
